import React, { FC, useEffect, useMemo, useState } from "react";
import FeatherIcon from "feather-icons-react";
import { useTranslation } from "react-i18next";
import { Tabs, Tab } from "@mui/material";

import {
  DM_RANGE_KEYS,
  DM_SUPPORTED_LANGUAGES,
  DmRangeKey,
} from "@constants/dmRanges";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@components/base";
import { Drawer } from "@components/common";

const RANGE_ITEM_COUNT = 5;
const TIME_HORIZON_ITEM_COUNT = 3;

const getItemCount = (key: DmRangeKey): number =>
  key.toLowerCase().includes("timehorizon")
    ? TIME_HORIZON_ITEM_COUNT
    : RANGE_ITEM_COUNT;

const getLabel = (key: DmRangeKey, index: number): string => {
  if (key.toLowerCase().includes("timehorizon")) {
    return `${index * 2 + 1}`;
  }

  return `${index + 1}`;
};

type RangeFields = Record<DmRangeKey, string[]>;

export type DmRangesFormValue = Record<string, RangeFields>;

const createEmptyRangeFields = (): RangeFields =>
  DM_RANGE_KEYS.reduce(
    (acc, key) => ({
      ...acc,
      [key]: Array.from({ length: getItemCount(key) }, () => ""),
    }),
    {} as RangeFields,
  );

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalizeRanges = (
  ranges: Record<string, unknown> | null,
): DmRangesFormValue => {
  const result = DM_SUPPORTED_LANGUAGES.reduce<DmRangesFormValue>(
    (acc, { code }) => {
      acc[code] = createEmptyRangeFields();

      return acc;
    },
    {} as DmRangesFormValue,
  );

  if (!isRecord(ranges)) {
    return result;
  }

  DM_SUPPORTED_LANGUAGES.forEach(({ code }) => {
    const languagePayload = ranges[code];

    if (!isRecord(languagePayload)) {
      return;
    }

    const normalized = createEmptyRangeFields();

    DM_RANGE_KEYS.forEach((key) => {
      const maybeValues = languagePayload[key];

      if (!Array.isArray(maybeValues)) {
        return;
      }

      const itemCount = getItemCount(key);

      normalized[key] = Array.from({ length: itemCount }, (_, idx) =>
        typeof maybeValues[idx] === "string" ? maybeValues[idx] : "",
      );
    });

    result[code] = normalized;
  });

  return result;
};

interface DoubleMaterialityRangesDrawerProps {
  open: boolean;
  onClose: () => void;
  initialRanges: Record<string, unknown> | null;
  onSave: (ranges: DmRangesFormValue) => Promise<void>;
  isSaving: boolean;
  canEdit: boolean;
}

const DoubleMaterialityRangesDrawer: FC<DoubleMaterialityRangesDrawerProps> = ({
  open,
  onClose,
  initialRanges,
  onSave,
  isSaving,
  canEdit,
}) => {
  const { t } = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState(
    DM_SUPPORTED_LANGUAGES[0].code,
  );

  const RANGE_LABEL_KEYS: Record<DmRangeKey, string> = {
    scale: t(
      "doubleMateriality.ranges.labels.scale",
      "Assess the scale of the impact",
    ),
    scope: t(
      "doubleMateriality.ranges.labels.scope",
      "Assess how widespread is the scope of the impact",
    ),
    remediability: t(
      "doubleMateriality.ranges.labels.remediability",
      "Assess the irremediable character of the impact",
    ),
    likelihood: t(
      "doubleMateriality.ranges.labels.likelihood",
      "Assess the likelihood of the impact",
    ),
    intensity: t(
      "doubleMateriality.ranges.labels.intensity",
      "Assess the intensity of the impact",
    ),
    timeHorizon: t(
      "doubleMateriality.ranges.labels.timeHorizon",
      "Assess the time horizon of the impact",
    ),
    riskLikelihood: t(
      "doubleMateriality.ranges.labels.riskLikelihood",
      "Assess the likelihood of the financial risk",
    ),
    riskMagnitude: t(
      "doubleMateriality.ranges.labels.riskMagnitude",
      "Assess the potential magnitude of financial risk",
    ),
    riskTimeHorizon: t(
      "doubleMateriality.ranges.labels.riskTimeHorizon",
      "Assess the time horizon in which you expect the financial risk to occur",
    ),
    opportunityLikelihood: t(
      "doubleMateriality.ranges.labels.opportunityLikelihood",
      "Assess the likelihood of the financial opportunity",
    ),
    opportunityMagnitude: t(
      "doubleMateriality.ranges.labels.opportunityMagnitude",
      "Assess the potential magnitude of financial opportunity",
    ),
    opportunityTimeHorizon: t(
      "doubleMateriality.ranges.labels.opportunityTimeHorizon",
      "Assess the time horizon in which you expect the financial opportunity to occur",
    ),
  };

  const normalizedRanges = useMemo(
    () => normalizeRanges(initialRanges),
    [initialRanges],
  );
  const [values, setValues] = useState<DmRangesFormValue>(normalizedRanges);

  useEffect(() => {
    if (open) {
      setValues(normalizedRanges);
      setSelectedLanguage((current) =>
        DM_SUPPORTED_LANGUAGES.some(({ code }) => code === current)
          ? current
          : DM_SUPPORTED_LANGUAGES[0].code,
      );
    }
  }, [open, normalizedRanges]);

  const handleChangeItem = (
    key: DmRangeKey,
    index: number,
    nextValue: string,
  ): void => {
    setValues((prev) => {
      const languageValues = prev[selectedLanguage] ?? createEmptyRangeFields();
      const nextLanguageValues: RangeFields = {
        ...languageValues,
        [key]: languageValues[key].map((item, idx) =>
          idx === index ? nextValue : item,
        ),
      };

      return {
        ...prev,
        [selectedLanguage]: nextLanguageValues,
      };
    });
  };

  const handleSave = (): void => {
    void onSave(values);
  };

  const currentValues = values[selectedLanguage] ?? createEmptyRangeFields();

  const renderRangeSection = (title: string, keys: DmRangeKey[]): React.ReactElement => (
    <Stack spacing={3}>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        textTransform="uppercase"
        fontWeight={600}
      >
        {title}
      </Typography>
      {keys.map((key) => {
        const itemCount = getItemCount(key);


        return (
          <Stack key={key} spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={500} fontSize={14}>
              {RANGE_LABEL_KEYS[key]}
            </Typography>
            <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
              {Array.from({ length: itemCount }).map((_, index) => (
                <TextField
                  key={index}
                  label={getLabel(key, index)}
                  value={currentValues[key][index] ?? ""}
                  onChange={(event) =>
                    handleChangeItem(key, index, event.target.value)
                  }
                  disabled={!canEdit}
                  size="small"
                  sx={{
                    flex:
                      index === itemCount - 1
                        ? "0 1 calc(50% - 12px)"
                        : "1 1 calc(50% - 12px)",
                  }}
                />
              ))}
            </Stack>
          </Stack>
        );
      })}
    </Stack>
  );

  return (
    <Drawer
      open={open}
      onClose={onClose}
      content={
        <>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            px={3}
            py={2}
          >
            <Typography variant="h6">
              {t(
                "doubleMateriality.ranges.drawerTitle",
                "Double materiality ranges",
              )}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <FeatherIcon icon="x" size={16} />
            </IconButton>
          </Stack>

          <Divider />

          <Stack spacing={4} px={3} py={3} sx={{ overflowY: "auto", flex: 1 }}>
            <Typography color="text.secondary" variant="body2">
              {t(
                "doubleMateriality.ranges.drawerDescription",
                "Describe the score ranges that best match your internal methodology. These values will be shown to team members filling in the survey.",
              )}
            </Typography>

            <Tabs
              value={selectedLanguage}
              onChange={(_, code: string) => setSelectedLanguage(code)}
              variant="scrollable"
              allowScrollButtonsMobile
            >
              {DM_SUPPORTED_LANGUAGES.map(({ code, label }) => (
                <Tab key={code} value={code} label={label} />
              ))}
            </Tabs>

            {renderRangeSection(
              t("doubleMateriality.ranges.impactSection", "Ranges for impact"),
              ["scope", "remediability", "likelihood", "intensity", "timeHorizon"],
            )}

            <Divider />

            {renderRangeSection(
              t("doubleMateriality.ranges.riskSection", "Ranges for risks"),
              ["riskLikelihood", "riskMagnitude", "riskTimeHorizon"],
            )}

            <Divider />

            {renderRangeSection(
              t(
                "doubleMateriality.ranges.opportunitySection",
                "Ranges for opportunity",
              ),
              [
                "opportunityLikelihood",
                "opportunityMagnitude",
                "opportunityTimeHorizon",
              ],
            )}

            {/* Keeping scale but potentially hidden if not needed in sections, or add a General section?
              Scale is used for external stakeholders rating. Adding a General section for it. */}
            <Divider />
            {renderRangeSection(
              t("doubleMateriality.ranges.generalSection", "General / External"),
              ["scale"],
            )}
          </Stack>

          <Divider />

          <Box py={2}>
            <Stack direction="row" justifyContent="flex-end" spacing={1.5}>
              <Button variant="text" onClick={onClose}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!canEdit || isSaving}
              >
                {t("common.save", "Save")}
              </Button>
            </Stack>
            {!canEdit && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mt={1}
              >
                {t(
                  "doubleMateriality.ranges.readOnlyNotice",
                  "Only workspace owners or managers can update ranges.",
                )}
              </Typography>
            )}
          </Box>
        </>
      }
    ></Drawer>
  );
};

export default DoubleMaterialityRangesDrawer;
